/**
 * Shared UI helpers + tiny primitives for the Workspace detail views.
 * Kept intentionally small — main components own their layout.
 */
import { formatDistanceToNow } from "date-fns";

export const BASE = `${process.env.REACT_APP_BACKEND_URL}/api/admin/workspace`;

export const CANDIDATE_STAGES = [
  { v: "new_candidate",    label: "New applicant",    color: "#94A3B8" },
  { v: "contacted",        label: "Contacted",        color: "#818CF8" },
  { v: "call_scheduled",   label: "Call Scheduled",   color: "#6366F1" },
  { v: "interviewed",      label: "Interviewed",      color: "#38BDF8" },
  { v: "profile_complete", label: "Profile complete", color: "#0EA5E9" },
  { v: "marketing",        label: "Marketing",        color: "#FBBF24" },
  { v: "submitted",        label: "Submitted",        color: "#F97316" },
  { v: "interview_booked", label: "Interview booked", color: "#FB7185" },
  { v: "offer",            label: "Offer",            color: "#A855F7" },
  { v: "placed",           label: "Placed",           color: "#22C55E" },
  { v: "talent_pool",      label: "Talent pool",      color: "#0D2F4D" },
];

// Employment Preparation & Coaching Package — lifecycle stages managed on the
// candidate record (separate from the recruitment `stage` above).
export const COACHING_STAGES = [
  "Payment Received",
  "Onboarding Required",
  "Strategy Session Booked",
  "CV in Progress",
  "CV Completed",
  "LinkedIn Completed",
  "Interview Coaching Booked",
  "Support in Progress",
  "Package Complete",
];


export const EMPLOYER_STATUSES = [
  { v: "prospect",         label: "Prospect",         color: "#94A3B8" },
  { v: "active_client",    label: "Active client",    color: "#22C55E" },
  { v: "vacancy_received", label: "Vacancy received", color: "#38BDF8" },
  { v: "candidates_sent",  label: "Candidates sent",  color: "#FBBF24" },
  { v: "interviewing",     label: "Interviewing",     color: "#F97316" },
  { v: "placement_made",   label: "Placement made",   color: "#A855F7" },
  { v: "follow_up",        label: "Follow-up",        color: "#0D2F4D" },
];

export const ROLE_STATUSES = [
  { v: "open",         label: "Open",         color: "#22C55E" },
  { v: "shortlisting", label: "Shortlisting", color: "#38BDF8" },
  { v: "paused",       label: "Paused",       color: "#A78BFA" },
  { v: "filled",       label: "Filled",       color: "#0D2F4D" },
  { v: "closed",       label: "Closed",       color: "#94A3B8" },
];

export const COMMS_CHANNELS = [
  { v: "call",       label: "Call" },
  { v: "email_out",  label: "Email sent" },
  { v: "email_in",   label: "Email received" },
  { v: "meeting",    label: "Meeting" },
  { v: "text",       label: "Text/SMS" },
  { v: "linkedin",   label: "LinkedIn message" },
  { v: "other",      label: "Other" },
];

export const DOC_KINDS = [
  { v: "cv",           label: "CV / Resume" },
  { v: "profile_photo", label: "Profile Photo" },
  { v: "police_check", label: "Police Check" },
  { v: "driver_licence", label: "Driver Licence" },
  { v: "certificate", label: "Certificate" },
  { v: "visa_id", label: "Visa / ID" },
  { v: "cover_letter", label: "Cover letter" },
  { v: "reference",    label: "Reference" },
  { v: "contract",     label: "Contract" },
  { v: "brief",        label: "Brief" },
  { v: "other",        label: "Other" },
];

export const stageMeta = (v) => CANDIDATE_STAGES.find((s) => s.v === v) || CANDIDATE_STAGES[0];
export const employerStatusMeta = (v) => EMPLOYER_STATUSES.find((s) => s.v === v) || EMPLOYER_STATUSES[0];
export const roleStatusMeta = (v) => ROLE_STATUSES.find((s) => s.v === v) || ROLE_STATUSES[0];
export const channelMeta = (v) => COMMS_CHANNELS.find((c) => c.v === v) || COMMS_CHANNELS[6];

export const cx = (...c) => c.filter(Boolean).join(" ");
export const empty = (v) => v === undefined || v === null || String(v).trim() === "";
export const csvList = (v) => (v || "").split(",").map((s) => s.trim()).filter(Boolean);

export const relTime = (iso) => {
  if (!iso) return "";
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true });
  } catch (_) {
    return iso;
  }
};

export const authHeader = (pw) => ({ headers: { "X-Admin-Password": pw } });

/** Coloured pill for stage / status. */
export function StagePill({ stage }) {
  const m = stageMeta(stage);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border whitespace-nowrap"
      style={{ borderColor: m.color, color: m.color, background: `${m.color}12` }}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

export function RoleStatusPill({ status }) {
  const m = roleStatusMeta(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border"
      style={{ borderColor: m.color, color: m.color, background: `${m.color}12` }}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

export function EmployerStatusPill({ status }) {
  const m = employerStatusMeta(status);
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border"
      style={{ borderColor: m.color, color: m.color, background: `${m.color}12` }}
    >
      <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
}

/** Consistent section header used across detail tabs. */
export function SectionHeader({ title, right }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-[11px] uppercase tracking-widest font-bold text-[#6F8FA8]">{title}</h4>
      {right}
    </div>
  );
}

/** Empty-state block. */
export function EmptyState({ text, action }) {
  return (
    <div className="bg-[#F2F2EF] rounded-xl p-6 text-center">
      <p className="text-sm text-[#6F8FA8]">{text}</p>
      {action}
    </div>
  );
}

/** Small stat block for headers. */
export function MiniStat({ label, value }) {
  return (
    <div className="text-center px-3">
      <p className="text-[10px] uppercase tracking-widest text-[#6F8FA8] font-bold">{label}</p>
      <p className="text-lg font-display font-extrabold text-[#0D2F4D]">{value ?? "—"}</p>
    </div>
  );
}
